import React, { useEffect, useRef, useState } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import '../../styles/quill-custom.css';


export default function BusinessDescriptionModal({ opened, onClose, initialContent, onSave }) {
  console.log('[Modal] Received initialContent prop:', initialContent);
  const editorRef = useRef(null);
  const quillInstance = useRef(null);
  const [content, setContent] = useState('');
  const [instanceId, setInstanceId] = useState(0);
  // isSaving gates the Save button + Cancel + backdrop-close while the
  // parent's onSave promise is in flight. The parent does an AI-backed
  // profession re-resolution on save which can take ~4 seconds — without
  // this, the modal looked frozen until it abruptly closed.
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!opened) {
      setInstanceId(prev => prev + 1);
    }
  }, [opened]);

useEffect(() => {
  if (opened && editorRef.current) {
    console.log('[Modal] opened:', opened);
    console.log('[Modal] incoming initialContent:', initialContent);

    // ✅ Full cleanup of prior instance and toolbar
    if (quillInstance.current) {
      const quill = quillInstance.current;

      // 🧼 Remove toolbar element explicitly
      const toolbar = quill.container?.previousSibling;
      if (toolbar?.classList?.contains('ql-toolbar')) {
        toolbar.remove();
      }

      quill.container.innerHTML = '';
      quillInstance.current = null;
    }

    console.log('[Quill] Creating new instance.');
    quillInstance.current = new Quill(editorRef.current, {
      theme: 'snow',
      placeholder: 'Describe your business here...',
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ script: 'sub' }, { script: 'super' }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ align: [] }],
          ['link', 'blockquote', 'clean'] // ❌ code-block removed
        ],
      },
    });
    const toolbar = document.querySelector('.ql-toolbar');
    if (toolbar) {
      toolbar.querySelector('.ql-header[value="1"]')?.setAttribute('title', 'Heading 1');
      toolbar.querySelector('.ql-header[value="2"]')?.setAttribute('title', 'Heading 2');
      toolbar.querySelector('.ql-header[value="3"]')?.setAttribute('title', 'Heading 3');
      toolbar.querySelector('.ql-header[value="false"]')?.setAttribute('title', 'Normal Text');
      toolbar.querySelector('.ql-bold')?.setAttribute('title', 'Bold');
      toolbar.querySelector('.ql-italic')?.setAttribute('title', 'Italic');
      toolbar.querySelector('.ql-underline')?.setAttribute('title', 'Underline');
      toolbar.querySelector('.ql-strike')?.setAttribute('title', 'Strikethrough');
      toolbar.querySelector('.ql-color')?.setAttribute('title', 'Text Color');
      toolbar.querySelector('.ql-background')?.setAttribute('title', 'Background Color');
      toolbar.querySelector('.ql-script[value="sub"]')?.setAttribute('title', 'Subscript');
      toolbar.querySelector('.ql-script[value="super"]')?.setAttribute('title', 'Superscript');
      toolbar.querySelector('.ql-list[value="ordered"]')?.setAttribute('title', 'Ordered List');
      toolbar.querySelector('.ql-list[value="bullet"]')?.setAttribute('title', 'Bullet List');
      toolbar.querySelector('.ql-align')?.setAttribute('title', 'Text Alignment');
      toolbar.querySelector('.ql-link')?.setAttribute('title', 'Insert Link');
      toolbar.querySelector('.ql-blockquote')?.setAttribute('title', 'Blockquote');
      toolbar.querySelector('.ql-clean')?.setAttribute('title', 'Clear Formatting');
    }

    const contentToSet = initialContent || '';
    quillInstance.current.root.innerHTML = contentToSet;
    setContent(contentToSet);

    const handler = () => {
      setContent(quillInstance.current.root.innerHTML);
    };
    quillInstance.current.on('text-change', handler);

    // ✅ Cleanup on modal close
    return () => {
      if (quillInstance.current) {
        quillInstance.current.off('text-change', handler);

        const quill = quillInstance.current;
        const toolbar = quill.container?.previousSibling;
        if (toolbar?.classList?.contains('ql-toolbar')) {
          toolbar.remove();
        }

        quill.container.innerHTML = '';
        quillInstance.current = null;
        if (editorRef.current) editorRef.current.innerHTML = '';
      }
    };
  }
}, [opened, initialContent]);




  // Await the parent's onSave so the spinner stays visible for the full
  // round-trip (the parent does a ~4s AI re-classification before
  // closing the modal). Guards against double-clicks via isSaving.
  const handleSave = async () => {
    if (isSaving) return;
    if (!quillInstance.current) return;
    const updatedHTML = quillInstance.current.root.innerHTML;
    setIsSaving(true);
    try {
      await onSave(updatedHTML);
    } finally {
      // Parent typically closes the modal in its own finally block, which
      // unmounts this component. The setState is here for the rare case
      // where the parent keeps the modal open after an error.
      setIsSaving(false);
    }
  };

  if (!opened) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-[1001]"
      // Backdrop close is disabled while saving so an accidental click
      // outside doesn't dismiss the modal mid-request.
      onClick={isSaving ? undefined : onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-semibold mb-4">Edit Your Business Description</h2>

        {/* Editor + saving overlay. The overlay sits on top of the Quill
            editor only (not the whole modal) so the user can still see
            their content underneath while it's being saved. */}
        <div className="relative">
          <div ref={editorRef} style={{ height: '300px' }} />
          {isSaving && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-[1px] rounded pointer-events-auto"
              aria-live="polite"
            >
              <div className="flex items-center gap-3 bg-white px-4 py-2.5 rounded-full shadow-md ring-1 ring-gray-200">
                <svg
                  className="w-5 h-5 animate-spin text-[#00aef7]"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
                  <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                </svg>
                <span className="text-sm font-medium text-gray-700">
                  Saving and re-analyzing&hellip;
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-4 gap-3">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-80 disabled:cursor-wait inline-flex items-center gap-2 min-w-[88px] justify-center"
          >
            {isSaving ? (
              <>
                <svg
                  className="w-4 h-4 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.35" strokeWidth="4" />
                  <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                </svg>
                Saving&hellip;
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
